/* Notification Center — Rule Detail page, ported from the standalone HTML
 * prototype's #page-rule-detail.  Opened by Edit (1 row selected) or by clicking
 * a rule name on the Notification Rules list.  8 tabs: General · Trigger
 * Condition · Targets (add/edit/delete) · Recipients · Delivery Method ·
 * Escalation · Email Template · Audit/History.  Demo data — the conditions are
 * built-in MDI defaults; the one configurable thing is recipients. */
import { useState, type ReactNode } from 'react';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { PURPOSE_LABEL, type NcRule, type Severity } from './data';

type Tab = 'general' | 'trigger' | 'targets' | 'recipients' | 'delivery' | 'escalation' | 'template' | 'audit';
const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'general', icon: 'ti-info-circle', label: 'General' },
  { key: 'trigger', icon: 'ti-bolt', label: 'Trigger Condition' },
  { key: 'targets', icon: 'ti-target', label: 'Targets' },
  { key: 'recipients', icon: 'ti-users-group', label: 'Recipients' },
  { key: 'delivery', icon: 'ti-send', label: 'Delivery Method' },
  { key: 'escalation', icon: 'ti-arrow-up-circle', label: 'Escalation' },
  { key: 'template', icon: 'ti-mail', label: 'Email Template' },
  { key: 'audit', icon: 'ti-history', label: 'Audit / History' },
];

interface DTarget { analyte: string; meta: string; condition: string; scope: string; enabled: boolean }
const SEED_TARGETS: DTarget[] = [
  { analyte: 'Lead (Pb)', meta: 'CAS 7439-92-1', condition: '[Result] ≥ [ClientPermitLimit] × 1.0', scope: 'Drinking Water · All clients', enabled: true },
  { analyte: 'Total Suspended Solids', meta: 'TSS · SM 2540D', condition: '[Result] ≥ [ClientPermitLimit] × 1.0', scope: 'Wastewater · All clients', enabled: true },
  { analyte: 'pH', meta: 'SM 4500-H+B', condition: '[Result] outside 6.0 – 9.0', scope: 'Wastewater · Permit clients', enabled: true },
  { analyte: 'E. coli', meta: 'Colilert · SM 9223B', condition: '[Result] is Positive', scope: 'Drinking Water · All clients', enabled: true },
  { analyte: 'Total Coliform', meta: 'Colilert · SM 9223B', condition: '[Result] is Positive', scope: 'Drinking Water · All clients', enabled: true },
];
const COND_OPTS = ['[Result] ≥ [ClientPermitLimit] × 1.0', '[Result] outside 6.0 – 9.0', '[Result] is Positive', '[Result] > 0 (presence)'];
const SCOPE_OPTS = ['Drinking Water · All clients', 'Wastewater · All clients', 'Wastewater · Permit clients', 'All matrices · All clients'];

function SevPill({ s }: { s: Severity }) {
  const label = s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';
  return <span className={`nc-sev ${s}`}><span className="dot" />{label}</span>;
}
function Info({ label, children }: { label: string; children: ReactNode }) {
  return <div className="nc-info-item"><div className="nc-ii-label">{label}</div><div className="nc-ii-value">{children}</div></div>;
}
function ToggleRow({ text, sub, on, onClick }: { text: string; sub: string; on: boolean; onClick: () => void }) {
  return <div className="nc-togrow"><div><div className="nc-togrow-text">{text}</div><div className="nc-togrow-sub">{sub}</div></div><span className={`nc-toggle${on ? ' on' : ''}`} role="button" onClick={onClick} /></div>;
}

export default function RuleDetailPage({ rule, categoryName, onBack, onDeleted }: {
  rule: NcRule; categoryName: string; onBack: () => void; onDeleted: () => void;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<Tab>('general');
  const [active, setActive] = useState(rule.enabled);
  const [targets, setTargets] = useState<DTarget[]>(SEED_TARGETS);
  const [editor, setEditor] = useState<{ idx: number | null; draft: DTarget } | null>(null);
  // General governance toggles
  const [gov, setGov] = useState({ ack: rule.ack ?? true, audit: true, capa: true, mdi: true });
  // Delivery / escalation toggles
  const [del, setDel] = useState({ suppress: true, bypass: true, portal: false, autoResolve: false });

  function commitEditor() {
    if (!editor) return;
    if (!editor.draft.analyte.trim()) { notify.warning('Enter an analyte / target name.'); return; }
    setTargets((cur) => editor.idx === null ? [...cur, editor.draft] : cur.map((t, j) => (j === editor.idx ? editor.draft : t)));
    notify.success(editor.idx === null ? 'Target added.' : 'Target updated.');
    setEditor(null);
  }
  function delTarget(i: number) {
    confirm({ title: 'Remove target?', message: `Remove “${targets[i]!.analyte}” from this rule?`, tone: 'danger', confirmLabel: 'Remove' })
      .then((ok) => { if (ok) { setTargets((cur) => cur.filter((_, j) => j !== i)); notify.success('Target removed.'); } });
  }
  function deleteRule() {
    confirm({ title: 'Delete rule?', message: `Delete “${rule.name}”? This cannot be undone.`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { notify.success('Rule deleted.'); onDeleted(); } });
  }

  const editorRow = (idx: number | null) => editor && editor.idx === idx ? (
    <tr key={`ed-${idx}`}>
      <td><input className="nc-finput nc-sm" placeholder="Analyte…" value={editor.draft.analyte} onChange={(e) => setEditor({ ...editor, draft: { ...editor.draft, analyte: e.target.value } })} /></td>
      <td><select className="nc-fselect nc-sm" value={editor.draft.condition} onChange={(e) => setEditor({ ...editor, draft: { ...editor.draft, condition: e.target.value } })}>{COND_OPTS.map((o) => <option key={o}>{o}</option>)}</select></td>
      <td><select className="nc-fselect nc-sm" value={editor.draft.scope} onChange={(e) => setEditor({ ...editor, draft: { ...editor.draft, scope: e.target.value } })}>{SCOPE_OPTS.map((o) => <option key={o}>{o}</option>)}</select></td>
      <td style={{ textAlign: 'center' }}><span className={`nc-toggle${editor.draft.enabled ? ' on' : ''}`} role="button" onClick={() => setEditor({ ...editor, draft: { ...editor.draft, enabled: !editor.draft.enabled } })} /></td>
      <td><div className="nc-rowacts"><button className="nc-iconbtn" title="Save" onClick={commitEditor}><i className="ti ti-check" /></button><button className="nc-iconbtn" title="Cancel" onClick={() => setEditor(null)}><i className="ti ti-x" /></button></div></td>
    </tr>
  ) : null;

  return (
    <div className="nc-root">
      <div className="nc-topbar">
        <div className="nc-title-wrap">
          <div className="nc-title-ic"><i className="ti ti-adjustments-horizontal" /></div>
          <div><div className="nc-title">Rule Detail</div><div className="nc-sub">{rule.name} · {rule.code}</div></div>
          <span className="nc-load"><i className="ti ti-bolt" /> Loaded in 0.3 sec</span>
        </div>
        <div className="nc-actions">
          <button className="ca-btn" onClick={() => notify.success('Rule duplicated.')}><i className="ti ti-copy" /> Duplicate</button>
          <button className="ca-btn" onClick={() => notify.info('All fields on this page are editable — make changes and click Save.')}><span className="tb-i tb-i-blue">✎</span> Edit</button>
          <button className="ca-btn" onClick={deleteRule}><span className="tb-i tb-i-red">🗑</span> Delete</button>
          <button className="ca-btn" onClick={() => notify.success('Rule refreshed.')} title="Refresh"><span className="tb-i tb-i-green">🔄</span> Refresh</button>
          <button className="ca-btn ca-btn-solid" title="Save"
            onClick={() => { void confirm({ title: 'Save', message: 'Do you want to Save?' }).then((ok) => { if (ok) notify.success('Saved successfully.'); }); }}>
            <span className="tb-i">💾</span> Save</button>
        </div>
      </div>

      <div className="nc-scroll">
        {/* detail header */}
        <div className="nc-dhead">
          <div className="nc-dcrumb" onClick={onBack}><i className="ti ti-arrow-left" /> Notification Rules</div>
          <div className="nc-dhead-top">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {rule.outbound ? <span className="nc-comm"><span className="dot" />Outbound</span> : rule.severity ? <SevPill s={rule.severity} /> : null}
                {rule.ack && <span className="nc-acktag">Ack required</span>}
                <span className="nc-mdibadge"><i className="ti ti-stack-2" /> Default · MDI</span>
              </div>
              <div className="nc-dname">{rule.name}</div>
              <div className="nc-dmeta">
                <span className="nc-dmeta-item"><span className="nc-dm-k">Code:</span> <span className="nc-mono">{rule.code}</span></span>
                <span className="nc-dmeta-item"><span className="nc-dm-k">Category:</span> {categoryName}</span>
                <span className="nc-dmeta-item"><span className="nc-dm-k">Type:</span> Standard default (MDI)</span>
                <span className="nc-dmeta-item"><span className="nc-dm-k">Editable:</span> Yes</span>
                <span className="nc-dmeta-item"><span className="nc-dm-k">Last edited:</span> 06/18/2026 11:24 · Selvi</span>
              </div>
            </div>
            <div className="nc-dstatus">
              <span className={`nc-toggle${active ? ' on' : ''}`} role="button" onClick={() => { setActive(!active); notify.success(active ? 'Rule disabled.' : 'Rule enabled.'); }} />
              <div><div className="nc-dsb-label">{active ? 'Active' : 'Disabled'}</div><div className="nc-dsb-sub">{active ? 'Evaluating live' : 'Not evaluating'}</div></div>
            </div>
          </div>
          <div className="nc-sstrip">
            <div className="nc-ss-icon"><i className="ti ti-stack-2" /></div>
            <div className="nc-ss-body">
              <div className="nc-ss-title">Standard default rule · Master Data Initialization (MDI)</div>
              <div className="nc-ss-sub">Comes pre-set as a standard default — conditions and thresholds are built in, and permit limits resolve live from each sample's client record. The one thing you configure is <strong>who receives the emails</strong>.</div>
            </div>
            <button className="ca-btn" style={{ whiteSpace: 'nowrap' }} onClick={() => setTab('recipients')}><i className="ti ti-users-group" /> Configure recipients</button>
          </div>
        </div>

        {/* tabs */}
        <div className="nc-tabs">
          {TABS.map((t) => (
            <div key={t.key} className={`nc-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
              <i className={`ti ${t.icon}`} /> {t.label}
              {t.key === 'targets' && <span className="nc-tab-count">{targets.length}</span>}
              {t.key === 'recipients' && <span className="nc-tab-count">3</span>}
            </div>
          ))}
        </div>

        {/* GENERAL */}
        {tab === 'general' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-info-circle" /> Rule Definition</div></div>
              <div className="nc-panel-body">
                <div className="nc-infogrid">
                  <Info label="Rule Name">{rule.name}</Info>
                  <Info label="Rule Code"><span className="nc-mono">{rule.code}</span></Info>
                  <Info label="Category">{categoryName}</Info>
                  <Info label="Notification Purpose"><span className={`nc-pp ${rule.purpose}`}>{PURPOSE_LABEL[rule.purpose]}</span></Info>
                  <Info label="Severity">{rule.severity ? <SevPill s={rule.severity} /> : <span className="nc-comm"><span className="dot" />Outbound</span>}</Info>
                  <Info label="Source">Standard default (MDI)</Info>
                </div>
              </div>
            </div>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-checkup-list" /> Compliance &amp; Governance</div></div>
              <div className="nc-panel-body">
                <ToggleRow text="Requires acknowledgment" sub="A person must explicitly acknowledge before this clears — recommended for regulatory exceedances." on={gov.ack} onClick={() => setGov({ ...gov, ack: !gov.ack })} />
                <ToggleRow text="Audit required" sub="Every change to this rule is recorded in the standard audit trail (IAuditTrailService)." on={gov.audit} onClick={() => setGov({ ...gov, audit: !gov.audit })} />
                <ToggleRow text="Corrective action required" sub="On firing, open a CAPA / corrective-action record — for violations and out-of-spec events." on={gov.capa} onClick={() => setGov({ ...gov, capa: !gov.capa })} />
                <ToggleRow text="Default enabled (MDI)" sub="Ships enabled in the standard default library when a new lab is initialised." on={gov.mdi} onClick={() => setGov({ ...gov, mdi: !gov.mdi })} />
              </div>
            </div>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-file-description" /> Description</div></div>
              <div className="nc-panel-body" style={{ fontSize: 12.5, color: 'var(--nc-text-mid)', lineHeight: 1.6 }}>
                Fires when any validated analytical result meets or exceeds the client-specific permit limit (or the configured early-warning fraction) for the monitored analyte. Required acknowledgment ensures a responsible official reviews before report release.
              </div>
            </div>
          </>
        )}

        {/* TRIGGER */}
        {tab === 'trigger' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-bolt" /> Trigger Condition</div><div className="nc-panel-sub">What the rule watches and when it is evaluated. The per-analyte thresholds live on the Targets tab.</div></div></div>
            <div className="nc-panel-body">
              <div className="nc-infogrid">
                <Info label="Watches">Analysis Result</Info>
                <Info label="Evaluation">Immediately on result validation (live)</Info>
                <Info label="Match mode">Any target matches (OR)</Info>
              </div>
              <div className="nc-form-hint" style={{ marginTop: 12 }}>Live rules evaluate on the <code>ResultValidated</code> domain event; scheduled rules (TAT, calibration, cert expiry) run on a recurring job.</div>
            </div>
          </div>
        )}

        {/* TARGETS */}
        {tab === 'targets' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd">
                <div><div className="nc-panel-title"><i className="ti ti-target" /> Monitored Targets</div><div className="nc-panel-sub">Each target is one analyte/condition this rule watches — the rule fires when <strong>any</strong> target matches a validated result.</div></div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><span style={{ fontSize: 11, color: 'var(--nc-text-light)' }}>Match mode:</span>
                  <select className="nc-fselect nc-sm"><option>Any target matches (OR)</option><option>All targets match (AND)</option></select></div>
              </div>
              <table className="nc-tgt-table" style={{ border: 'none', borderRadius: 0 }}>
                <thead><tr><th style={{ width: '22%' }}>Target / Analyte</th><th style={{ width: '34%' }}>Condition</th><th style={{ width: '26%' }}>Scope</th><th style={{ width: '9%', textAlign: 'center' }}>Enabled</th><th style={{ width: '9%', textAlign: 'right' }}>Actions</th></tr></thead>
                <tbody>
                  {targets.map((t, i) => editor && editor.idx === i ? editorRow(i) : (
                    <tr key={i}>
                      <td><div className="nc-tgt-name">{t.analyte}</div><div className="nc-tgt-scope">{t.meta}</div></td>
                      <td><span className="nc-tgt-cond">{t.condition}</span></td>
                      <td><span className="nc-tgt-scope">{t.scope}</span></td>
                      <td style={{ textAlign: 'center' }}><span className={`nc-toggle${t.enabled ? ' on' : ''}`} role="button" onClick={() => setTargets((cur) => cur.map((x, j) => j === i ? { ...x, enabled: !x.enabled } : x))} /></td>
                      <td><div className="nc-rowacts"><button className="nc-iconbtn" title="Edit" onClick={() => setEditor({ idx: i, draft: { ...t } })}><i className="ti ti-edit" /></button><button className="nc-iconbtn del" title="Remove" onClick={() => delTarget(i)}><i className="ti ti-trash" /></button></div></td>
                    </tr>
                  ))}
                  {editor && editor.idx === null && editorRow(null)}
                  {targets.length === 0 && !editor && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--nc-text-light)', padding: 22 }}>No targets — add one to start monitoring.</td></tr>}
                </tbody>
              </table>
              <div className="nc-add-target-bar"><span className="nc-addbtn" onClick={() => setEditor({ idx: null, draft: { analyte: '', meta: '', condition: COND_OPTS[0]!, scope: SCOPE_OPTS[0]!, enabled: true } })}><i className="ti ti-plus" /> Add monitoring target</span></div>
            </div>
            <div className="nc-form-hint" style={{ padding: '0 4px' }}>Targets read live from the existing LIMS analyte / permit-limit tables — no limit values are re-entered here. Set a multiplier below 1.0 on any target to create an early-warning before the limit is breached.</div>
          </>
        )}

        {/* RECIPIENTS */}
        {tab === 'recipients' && (
          <div className="nc-panel">
            <div className="nc-panel-hd">
              <div><div className="nc-panel-title"><i className="ti ti-users-group" /> Recipients</div><div className="nc-panel-sub"><strong>The setting you configure for this lab.</strong> Who is notified, and on which channels, when this rule fires.</div></div>
              <button className="ca-btn" onClick={() => notify.info('Add a recipient on the Recipient Groups screen, then attach the group here.')}><span className="tb-i">＋</span> Add recipient</button>
            </div>
            <div className="nc-panel-body">
              {[{ av: 'LD', name: 'Lab Director', kind: 'role', desc: 'Resolves to current Lab Director', email: true },
                { av: 'QA', name: 'QA Manager', kind: 'role', desc: 'Resolves to current QA Manager', email: true },
                { av: 'AS', name: 'Assigned Salesperson', kind: 'dynamic', desc: 'Client.AssignedSalesperson on the source record', email: false }].map((r) => (
                <div key={r.name} className="nc-recip-row">
                  <div className="nc-recip-left"><div className="nc-recip-av">{r.av}</div><div><div className="nc-recip-name">{r.name}</div><div className="nc-recip-type"><span className={`nc-rt ${r.kind}`}>{r.kind === 'role' ? 'Role' : 'Dynamic'}</span> {r.desc}</div></div></div>
                  <div style={{ display: 'flex', gap: 6 }}><span className="nc-chan on">IN-APP</span><span className={`nc-chan${r.email ? ' on' : ''}`}>EMAIL</span></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DELIVERY */}
        {tab === 'delivery' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-send" /> Delivery Method</div><div className="nc-panel-sub">How and when the notification is delivered.</div></div></div>
            <div className="nc-panel-body">
              <div className="nc-form-group">
                <label className="nc-flabel">Notification type</label>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  {['Dashboard', 'In-App (bell)', 'Email', 'SMS', 'Teams'].map((c, i) => <label key={c} className="nc-check"><input type="checkbox" defaultChecked={i < 3} /> {c}</label>)}
                </div>
              </div>
              <div className="nc-form-row" style={{ marginBottom: 16 }}>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel">Delay / repeat</label><select className="nc-fselect"><option>Immediate</option><option>After 1 hour</option><option>Daily digest</option><option>Repeat every 4 hours until acknowledged</option></select></div>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel">Business hours</label><select className="nc-fselect"><option>Send any time (24/7)</option><option>Business hours only (Mon–Fri 08:00–17:00)</option></select></div>
              </div>
              <ToggleRow text="Suppress duplicate firing" sub="Re-occurrence on the same source record increments a repeat counter (×N) instead of sending again." on={del.suppress} onClick={() => setDel({ ...del, suppress: !del.suppress })} />
              <ToggleRow text="Bypass digest batching" sub="Send immediately even if severity would normally allow batching." on={del.bypass} onClick={() => setDel({ ...del, bypass: !del.bypass })} />
              <ToggleRow text="Client visible (portal)" sub="Whether the client portal can see this notification. Off for internal-only alerts." on={del.portal} onClick={() => setDel({ ...del, portal: !del.portal })} />
              <ToggleRow text="Auto-resolve when condition clears" sub="If a later validated result no longer meets the condition, mark it resolved automatically." on={del.autoResolve} onClick={() => setDel({ ...del, autoResolve: !del.autoResolve })} />
            </div>
          </div>
        )}

        {/* ESCALATION */}
        {tab === 'escalation' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-arrow-up-circle" /> Escalation</div><div className="nc-panel-sub">If not acknowledged in time, escalate to a higher level.</div></div></div>
            <div className="nc-panel-body">
              <div className="nc-form-row" style={{ marginBottom: 16 }}>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel">Escalation level</label><select className="nc-fselect" defaultValue="Manager"><option>None</option><option>Supervisor</option><option>Manager</option><option>QA</option><option>Lab Director</option></select></div>
                <div className="nc-form-group" style={{ marginBottom: 0 }}><label className="nc-flabel">Escalate after</label><select className="nc-fselect" defaultValue="1 hour"><option>30 minutes</option><option>1 hour</option><option>4 hours</option><option>End of shift</option></select></div>
              </div>
              <ToggleRow text="Escalate only if acknowledgment required" sub="Skip escalation for informational notifications that don't need a sign-off." on={gov.ack} onClick={() => setGov({ ...gov, ack: !gov.ack })} />
            </div>
          </div>
        )}

        {/* TEMPLATE */}
        {tab === 'template' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-mail" /> Email Template</div><div className="nc-panel-sub">The template used to render this rule's email. Manage templates on the Email Templates screen.</div></div></div>
            <div className="nc-panel-body">
              <div className="nc-form-group"><label className="nc-flabel">Template</label><select className="nc-fselect"><option>Critical Exceedance (Result Exceedance)</option><option>— Category default —</option></select></div>
              <div className="nc-form-group">
                <label className="nc-flabel">Preview</label>
                <div style={{ border: '1px solid var(--nc-border)', borderRadius: 8, padding: '12px 14px', background: '#fafbfc', fontSize: 12, color: 'var(--nc-text-mid)', lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 'bold', color: 'var(--nc-text)', marginBottom: 6 }}>[ALPACA] Critical: {'{{RuleName}}'} — {'{{SampleId}}'}</div>
                  A critical result was recorded for {'{{Client}}'}.<br />Sample: {'{{SampleId}}'} · Parameter: {'{{Parameter}}'}<br />Result: {'{{Result}}'} (limit {'{{Limit}}'}) · Due: {'{{DueDate}}'}
                </div>
                <div className="nc-form-hint">Tokens resolve at send time. Edit the template on the Email Templates screen.</div>
              </div>
            </div>
          </div>
        )}

        {/* AUDIT */}
        {tab === 'audit' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-history" /> Configuration Audit</div><div className="nc-panel-sub">Read-only change history for this rule.</div></div></div>
              <table className="nc-tbl" style={{ border: 'none' }}>
                <thead><tr><th>When</th><th>User</th><th>Change</th></tr></thead>
                <tbody>
                  <tr><td className="nc-mono">06/18/2026 11:24</td><td>Selvi</td><td>Recipients: added QA Manager</td></tr>
                  <tr><td className="nc-mono">06/10/2026 14:02</td><td>Wilson</td><td>Severity: Warning → Critical</td></tr>
                  <tr><td className="nc-mono">06/02/2026 09:15</td><td>Wilson</td><td>Rule created (seeded from MDI default)</td></tr>
                </tbody>
              </table>
            </div>
            <div className="nc-infogrid" style={{ marginTop: 4 }}>
              <Info label="Created">06/02/2026 09:15 · Wilson</Info>
              <Info label="Last edited">06/18/2026 11:24 · Selvi</Info>
              <Info label="Full audit trail"><span className="nc-rowlink">Open in Audit Trail Log →</span></Info>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
